import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setMessengerShown, startViewingHeartbeat } from '../messengerOnScreen';
import { startDeviceActivity } from '../deviceActivity';
import { DEFAULT_NAV } from '../utils/sidebarMenu';
import { canSeeMenu } from '../utils/menuAccess';

const SPLIT_RATIO_KEY = 'gwSplitRatio';
const SPLIT_LAST_APP_KEY = 'gwSplitLastApp';
const MIN_RATIO = 25;
const MAX_RATIO = 75;
const MIN_SPLIT_WIDTH = 1100;
const SIDE_HEADER_H = 30;

// 보조 패널 대상은 사이드바 DEFAULT_NAV 에서 자동으로 만든다(#1471).
// 메인·사용 안내·내 정보·관리자는 보조 패널 대상이 아니다(현행 유지). **새 메뉴는 DEFAULT_NAV 에 넣으면
// 저절로 보조 패널이 된다** — 빼야 할 메뉴만 여기 넣는다.
// 농심DAS(pdas)는 작업 화면(PdasJobPage onScanBlur)이 포커스를 늘 스캔 칸으로 되돌려 보조 패널에 두면
// 주 화면 입력칸에 타이핑할 수 없다 — 스캐너 작업은 전체 화면 작업이라 뺀다.
// 센터 메뉴는 보조 패널 대상이 아니다(지금 그대로) — DEFAULT_NAV '센터' 그룹에 들어와도 뺀다(2026-10-23).
export const SPLIT_EXCLUDED_KEYS = new Set([
  'home', 'help', 'my', 'admin', 'pdas', 'center-all', 'center-daily', 'center-shippers', 'center-perf',
]);

// 임베드(iframe) 메뉴 — 보조 패널에서는 EmbedHost 가 그린다. 정본은 EmbedHost.jsx EMBED_APPS 지만
// EmbedHost 가 이 파일을 import 해 순환이 되므로 손으로 둔다. 임베드 메뉴를 새로 만들면 여기도 넣는다 —
// 시험(SplitShell.test.jsx)이 알려 준다.
const EMBED_KEYS = new Set(['messenger', 'drive', 'calendar', 'mail2', 'talk']);

// DEFAULT_NAV 값과 다르게 보여야 하는 것만(기존 값 유지). chat 의 메뉴 경로는 '/' 지만 보조 패널은 '/chat'.
// 메일은 Roundcube('mail2') 한 곳으로 모았다(마스터 2026-08-24) — key 'mail2' · 라벨 '메일'은 DEFAULT_NAV 그대로.
const SPLIT_OVERRIDES = {
  chat: { path: '/chat' },
  documents: { title: 'AI 학습자료' },
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// 순수 함수 — 사이드바 메뉴 배열(DEFAULT_NAV 모양)에서 { key: { title, path, kind } } 를 만든다.
// 대상 = key·path 가 있는 항목({section} 제외) − SPLIT_EXCLUDED_KEYS. 같은 key 가 둘이면 앞의 것.
export function buildSplitApps(nav) {
  const apps = {};
  (Array.isArray(nav) ? nav : []).forEach((it) => {
    if (!it || it.section !== undefined) return;
    if (typeof it.key !== 'string' || !it.key || typeof it.path !== 'string' || !it.path) return;
    if (SPLIT_EXCLUDED_KEYS.has(it.key) || hasOwn(apps, it.key)) return;
    apps[it.key] = {
      title: it.label,
      path: it.path,
      kind: EMBED_KEYS.has(it.key) ? 'embed' : 'page',
      ...(hasOwn(SPLIT_OVERRIDES, it.key) ? SPLIT_OVERRIDES[it.key] : {}),
    };
  });
  return apps;
}

export const SPLIT_APPS = buildSplitApps(DEFAULT_NAV);

const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/verify-email',
  '/unlock-account',
  '/forgot-password',
  '/reset-password',
  '/authorize',
]);

const defaultContext = {
  sideApp: null,
  ratio: 50,
  slotRects: { main: null, side: null },
  canUseSplit: false,
  busy: false,
  renderSlot: 'main',
  mainApp: null,
  openSideApp: () => {},
  closeSideApp: () => {},
  swapPanes: () => {},
  canSwap: false,
  setBusy: () => {},
};

const SplitContext = createContext(defaultContext);

export function useSplitView() {
  return useContext(SplitContext) || defaultContext;
}

export function canOpenSplitApp(app) {
  return !!SPLIT_APPS[app];
}

export function isEmbedSplitApp(app) {
  return SPLIT_APPS[app]?.kind === 'embed';
}

export function getSplitAppTitle(app) {
  return SPLIT_APPS[app]?.title || '';
}

function readStoredRatio() {
  try {
    const n = Number(localStorage.getItem(SPLIT_RATIO_KEY));
    return n >= MIN_RATIO && n <= MAX_RATIO ? n : 50;
  } catch (e) {
    return 50;
  }
}

// 경로 → 보조 패널 앱 key. '/' 는 chat · 앱 경로와 정확히 같으면 그 앱 · 아니면 «앱 경로 + '/'» 로
// 시작하는 앱 중 경로가 가장 긴 것(/contracts/5 → contracts · /tasks/7/settings → taskpm) · 없으면 null.
export function getCurrentMainApp(pathname) {
  if (typeof pathname !== 'string' || !pathname) return null;
  if (pathname === '/') return 'chat';
  const keys = Object.keys(SPLIT_APPS);
  const exact = keys.find((key) => SPLIT_APPS[key].path === pathname);
  if (exact) return exact;
  let best = null;
  keys.forEach((key) => {
    const path = SPLIT_APPS[key].path;
    if (!path || path === '/' || !pathname.startsWith(`${path}/`)) return;
    if (!best || path.length > SPLIT_APPS[best].path.length) best = key;
  });
  return best;
}

// App.jsx 라우트 가드(MenuRoute)의 readCachedUser 와 같은 값 — App 을 import 하면 순환이라 같은 몸체를 둔다.
function readCachedUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
}

// 이 값들은 EmbedHost 가 position:fixed 슬롯의 left/top/width/height 에 **CSS px 로** 그대로 넣는다.
// getBoundingClientRect() 는 <html> 의 zoom(모바일 확대, MobileScale)이 **이미 반영된** 화면 px 이라,
// 그대로 쓰면 확대가 두 번 곱해진다 → 375px 화면에서 임베드가 562px 로 그려져 오른쪽이 잘렸다
// (2026-08-21 실측: 기본 배율 150% 에서 드라이브·메신저 로딩 화면이 화면 밖으로 밀림).
// 그래서 여기서 zoom 으로 나눠 CSS px 로 되돌린다. 데스크톱(zoom=1)에서는 값이 그대로다.
function getRect(node) {
  if (!node) return null;
  const r = node.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  let zoom = 1;
  try {
    const z = parseFloat(getComputedStyle(document.documentElement).zoom);
    if (Number.isFinite(z) && z > 0) zoom = z;
  } catch (e) { /* 구형 브라우저 — 확대 미적용으로 본다 */ }
  return {
    left: r.left / zoom,
    top: r.top / zoom,
    width: r.width / zoom,
    height: r.height / zoom,
  };
}

function getCanUseSplit() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= MIN_SPLIT_WIDTH;
}

export default function SplitShell({ children, overlay = null, renderSideApp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const shellRef = useRef(null);
  const mainRef = useRef(null);
  const mainBodyRef = useRef(null);
  const sideBodyRef = useRef(null);
  const measureFrameRef = useRef(null);
  const [ratio, setRatio] = useState(readStoredRatio);
  const [sideApp, setSideApp] = useState(null);
  // 보조 패널 라우터 — key 가 바뀌면 SidePaneRouter 를 새 initialPath 로 다시 만든다 · path = 보조 패널이 지금 보는 위치.
  // 🔴 sideApp 을 바꾸는 핸들러 안에서 함께 정한다 — [sideApp] effect 로 초기화하면 자식 effect(onLocationChange)가
  //    먼저 돌아 덮어쓴다.
  const [pane, setPane] = useState({ key: 0, app: null, initialPath: null, path: null });
  const [slotRects, setSlotRects] = useState({ main: null, side: null });
  const [canUseSplit, setCanUseSplit] = useState(getCanUseSplit);
  const [manualBusy, setManualBusy] = useState(false);
  const [observedBusy, setObservedBusy] = useState(false);
  const mainApp = getCurrentMainApp(location.pathname);
  const busy = manualBusy || observedBusy;
  // 전환·주 패널 닫기가 누르는 순간의 값을 읽는다 — 콜백 의존성에 넣으면 보조 패널 안 이동마다
  // 문맥(baseContext)이 바뀌어 주 화면 소비자가 다시 그려진다.
  const paneRef = useRef(pane);
  const mainPathRef = useRef(`${location.pathname}${location.search}`);
  useEffect(() => { paneRef.current = pane; }, [pane]);
  useEffect(() => { mainPathRef.current = `${location.pathname}${location.search}`; }, [location.pathname, location.search]);

  const measure = useCallback(() => {
    if (measureFrameRef.current) cancelAnimationFrame(measureFrameRef.current);
    measureFrameRef.current = requestAnimationFrame(() => {
      measureFrameRef.current = null;
      setSlotRects({
        main: getRect(mainBodyRef.current),
        side: sideApp ? getRect(sideBodyRef.current) : null,
      });
    });
  }, [sideApp]);

  useLayoutEffect(() => {
    measure();
    const nodes = [shellRef.current, mainRef.current, mainBodyRef.current, sideBodyRef.current].filter(Boolean);
    const ResizeObs = typeof ResizeObserver !== 'undefined' ? ResizeObserver : null;
    const ro = ResizeObs ? new ResizeObs(measure) : null;
    const visualViewport = window.visualViewport;
    if (ro) nodes.forEach((node) => ro.observe(node));
    window.addEventListener('resize', measure);
    if (visualViewport) {
      visualViewport.addEventListener('resize', measure);
      visualViewport.addEventListener('scroll', measure);
    }
    return () => {
      if (measureFrameRef.current) cancelAnimationFrame(measureFrameRef.current);
      measureFrameRef.current = null;
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', measure);
        visualViewport.removeEventListener('scroll', measure);
      }
    };
  }, [measure, ratio, sideApp]);

  useEffect(() => {
    const onResize = () => setCanUseSplit(getCanUseSplit());
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!canUseSplit && sideApp) setSideApp(null);
  }, [canUseSplit, sideApp]);

  // 같은 앱 양쪽 금지 — 주 화면이 보조 패널과 같은 앱으로 가면(예: 보조 패널 계약 관리를 연 채 주 화면에서
  // 계약 링크로 /contracts/5) 보조 패널을 닫는다. 응답 생성 중(busy)이면 닫지 않는다 — 보조 채팅 스트림이 끊기지 않게.
  // 🔴 BrowserRouter 에 future.v7_startTransition 을 켜면 swapPanes 의 setSideApp 과 navigate 가 한 렌더로 묶이지
  //    않아, 전환 중간 렌더에서 이 effect 가 방금 한 전환을 깰 수 있다 — 그 플래그를 켤 때 이 effect 를 다시 본다.
  useEffect(() => {
    if (sideApp && sideApp === mainApp && !busy) setSideApp(null);
  }, [busy, mainApp, sideApp]);

  // 메신저가 메인 또는 옆 창에 떠 있는가 — «보는 중»이면 서버가 메신저 푸시를 건너뛴다(messengerOnScreen.js).
  useEffect(() => {
    setMessengerShown(mainApp === 'messenger' || sideApp === 'messenger');
  }, [mainApp, sideApp]);

  // 🔴 하트비트보다 먼저 선언한다 — 언마운트 정리는 선언 순서라 이게 먼저 돌아야 false 신호가 나간다.
  useEffect(() => () => setMessengerShown(false), []);

  useEffect(() => startViewingHeartbeat(), []);

  // 이 기기에서 지금 쓰는 중(보임+포커스+최근 입력) — 서버가 메신저 푸시를 쓰는 기기엔 바로, 나머지는 미뤄 보낸다(deviceActivity.js).
  useEffect(() => startDeviceActivity(), []);

  useEffect(() => {
    if (PUBLIC_PATHS.has(location.pathname) || !localStorage.getItem('token')) {
      setSideApp(null);
      setManualBusy(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const root = shellRef.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;
    const update = () => {
      setObservedBusy(!!root.querySelector('button[title="응답 생성을 중지합니다"]'));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['title'] });
    return () => observer.disconnect();
  }, []);

  const persistRatio = useCallback((next) => {
    const clamped = Math.min(MAX_RATIO, Math.max(MIN_RATIO, next));
    setRatio(clamped);
    try { localStorage.setItem(SPLIT_RATIO_KEY, String(Math.round(clamped * 10) / 10)); } catch (e) { /* */ }
  }, []);

  // 보조 패널 라우터를 새로 시작한다(앱이 바뀌거나 전환할 때) — key 를 올려 SidePaneRouter 를 다시 만든다.
  const seedPane = useCallback((app, initialPath) => {
    setPane((p) => ({ key: p.key + 1, app, initialPath, path: initialPath }));
  }, []);

  // 보조 패널이 지금 보는 위치 — page 앱이면 보조 라우터가 알려 준 하위 위치(/contracts/5 등),
  // 아직 콜백 전이거나 임베드면 앱 경로.
  const sidePathFor = useCallback((app) => {
    const p = paneRef.current;
    return app && p.app === app && p.path && !isEmbedSplitApp(app) ? p.path : SPLIT_APPS[app]?.path;
  }, []);

  const openSideApp = useCallback((app) => {
    if (!canUseSplit || !canOpenSplitApp(app) || app === mainApp) return;
    if (app !== sideApp) seedPane(app, SPLIT_APPS[app].path);
    setSideApp(app);
    try { localStorage.setItem(SPLIT_LAST_APP_KEY, app); } catch (e) { /* */ }
    requestAnimationFrame(measure);
  }, [canUseSplit, mainApp, measure, seedPane, sideApp]);

  const closeSideApp = useCallback(() => {
    setSideApp(null);
    requestAnimationFrame(measure);
  }, [measure]);

  // 주 패널 닫기 — 보조 패널이 보던 위치(하위 화면 포함)를 주 화면으로 넘긴다.
  const closeMainApp = useCallback(() => {
    if (!sideApp) return;
    navigate(sidePathFor(sideApp));
    setSideApp(null);
    requestAnimationFrame(measure);
  }, [measure, navigate, sideApp, sidePathFor]);

  const canSwap = !!(sideApp && mainApp && sideApp !== mainApp && !busy);
  // 주/보조 전환 — 주 화면은 보조 패널이 보던 위치로, 보조 패널은 직전 주 화면 위치(경로+검색어)로.
  const swapPanes = useCallback(() => {
    if (!canSwap) return;
    const target = sidePathFor(sideApp);
    const previousMain = mainApp;
    const previousPath = isEmbedSplitApp(previousMain) ? SPLIT_APPS[previousMain].path : mainPathRef.current;
    seedPane(previousMain, previousPath);
    setSideApp(previousMain);
    navigate(target);
    try { localStorage.setItem(SPLIT_LAST_APP_KEY, previousMain); } catch (e) { /* */ }
    requestAnimationFrame(measure);
  }, [canSwap, mainApp, measure, navigate, seedPane, sideApp, sidePathFor]);

  // 보조 라우터가 자기 앱 안에서 움직였다 — 그 위치를 기억한다(전환·주 패널 닫기가 쓴다).
  const paneKey = pane.key;
  const onSideLocationChange = useCallback((path) => {
    if (typeof path !== 'string' || !path) return;
    setPane((p) => (p.key !== paneKey || p.path === path ? p : { ...p, path }));
  }, [paneKey]);

  // 보조 패널이 자기 앱 밖으로 나갔다(SidePaneRouter §3-1) — to 가 null 이면 보조 패널만 닫는다.
  // 대상 앱을 볼 권한이 없으면 주 화면은 그대로 두고 보조 패널만 닫는다(주 화면을 /home 으로 보내지 않게).
  const escapeSidePane = useCallback((to, state) => {
    if (typeof to === 'string' && to) {
      const targetApp = getCurrentMainApp(to.split(/[?#]/)[0]);
      if (!targetApp || canSeeMenu(readCachedUser(), targetApp)) {
        if (state !== undefined && state !== null) navigate(to, { state });
        else navigate(to);
      }
    }
    setSideApp(null);
    requestAnimationFrame(measure);
  }, [measure, navigate]);

  const startDrag = useCallback((e) => {
    if (!sideApp) return;
    e.preventDefault();
    const shell = shellRef.current;
    if (!shell) return;
    const bounds = shell.getBoundingClientRect();
    const overlayNode = document.createElement('div');
    overlayNode.style.cssText = 'position:fixed;inset:0;z-index:2147483647;cursor:col-resize';
    document.body.appendChild(overlayNode);
    const onMove = (ev) => {
      const next = ((ev.clientX - bounds.left) / bounds.width) * 100;
      persistRatio(next);
      measure();
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      if (overlayNode.parentNode) overlayNode.parentNode.removeChild(overlayNode);
      measure();
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [measure, persistRatio, sideApp]);

  const baseContext = useMemo(() => ({
    sideApp,
    ratio,
    slotRects,
    canUseSplit,
    busy,
    renderSlot: 'main',
    mainApp,
    openSideApp,
    closeSideApp,
    swapPanes,
    canSwap,
    setBusy: setManualBusy,
  }), [busy, canSwap, canUseSplit, closeSideApp, mainApp, openSideApp, ratio, sideApp, slotRects, swapPanes]);

  const sideContext = useMemo(() => ({
    ...baseContext,
    renderSlot: 'side',
  }), [baseContext]);

  const sideTitle = getSplitAppTitle(sideApp);
  const sideHeight = slotRects.side ? `${Math.round(slotRects.side.height)}px` : `calc(100dvh - ${SIDE_HEADER_H}px)`;
  const mainTitle = getSplitAppTitle(mainApp);
  const mainHeight = slotRects.main ? `${Math.round(slotRects.main.height)}px` : `calc(100dvh - ${SIDE_HEADER_H}px)`;

  return (
    <SplitContext.Provider value={baseContext}>
      <div
        ref={shellRef}
        data-gw-split-shell
        style={{ display: 'flex', width: '100%', height: 'var(--gw-app-h, 100dvh)', minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative', background: '#fff' }}
      >
        <div
          ref={mainRef}
          data-gw-split-main
          style={{ flex: sideApp ? `0 0 ${ratio}%` : '1 1 100%', minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}
        >
          {sideApp && (
            <div style={{ height: SIDE_HEADER_H, minHeight: SIDE_HEADER_H, display: 'flex', alignItems: 'center', gap: '6px', padding: '0 8px', borderBottom: '1px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
              <div title={mainTitle} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: 700, color: '#374151' }}>{mainTitle}</div>
              <button
                type="button"
                onClick={swapPanes}
                disabled={!canSwap}
                title={busy ? '응답 생성 중에는 전환할 수 없습니다' : (canSwap ? '주/보조 전환' : '현재 화면은 전환할 수 없습니다')}
                aria-label="주/보조 전환"
                style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', background: 'transparent', color: canSwap ? '#374151' : '#9ca3af', cursor: canSwap ? 'pointer' : 'not-allowed', fontSize: '15px', lineHeight: 1 }}
              >
                ⇄
              </button>
              <button
                type="button"
                onClick={closeMainApp}
                title="닫기"
                aria-label="주 패널 닫기"
                style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', background: 'transparent', color: '#374151', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          )}
          <div ref={mainBodyRef} style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', position: 'relative', ...(sideApp ? { '--gw-app-h': mainHeight } : {}) }}>
            {children}
          </div>
        </div>
        {sideApp && (
          <>
            <div
              role="separator"
              aria-orientation="vertical"
              onPointerDown={startDrag}
              style={{ flex: '0 0 6px', width: '6px', cursor: 'col-resize', background: '#e5e7eb', zIndex: 6 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#cbd5e1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
            />
            <section
              data-gw-split-side
              aria-label="보조 패널"
              style={{ flex: `0 0 calc(${100 - ratio}% - 6px)`, minWidth: 0, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', borderLeft: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            >
              <div style={{ height: SIDE_HEADER_H, minHeight: SIDE_HEADER_H, display: 'flex', alignItems: 'center', gap: '6px', padding: '0 8px', borderBottom: '1px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
                <div title={sideTitle} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px', fontWeight: 700, color: '#374151' }}>{sideTitle}</div>
                <button
                  type="button"
                  onClick={swapPanes}
                  disabled={!canSwap}
                  title={busy ? '응답 생성 중에는 전환할 수 없습니다' : (canSwap ? '주/보조 전환' : '현재 화면은 전환할 수 없습니다')}
                  aria-label="주/보조 전환"
                  style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', background: 'transparent', color: canSwap ? '#374151' : '#9ca3af', cursor: canSwap ? 'pointer' : 'not-allowed', fontSize: '15px', lineHeight: 1 }}
                >
                  ⇄
                </button>
                <button
                  type="button"
                  onClick={closeSideApp}
                  title="닫기"
                  aria-label="보조 패널 닫기"
                  style={{ width: '24px', height: '24px', border: 'none', borderRadius: '4px', background: 'transparent', color: '#374151', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
              <div ref={sideBodyRef} style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', position: 'relative', '--gw-app-h': sideHeight }}>
                <SplitContext.Provider value={sideContext}>
                  {renderSideApp ? renderSideApp(sideApp, {
                    paneKey: pane.app === sideApp ? pane.key : undefined,
                    initialPath: pane.app === sideApp && pane.initialPath ? pane.initialPath : SPLIT_APPS[sideApp]?.path,
                    onEscape: escapeSidePane,
                    onLocationChange: onSideLocationChange,
                  }) : null}
                </SplitContext.Provider>
              </div>
            </section>
          </>
        )}
      </div>
      {overlay}
    </SplitContext.Provider>
  );
}
